import axios, {
	AxiosError,
	AxiosRequestConfig,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from 'axios';
import { getJWT } from '@bynder/js-access-token-lib';
import { CSRF } from './csrf';

async function addToken(config: InternalAxiosRequestConfig) {
	if (process.env.NODE_ENV === 'development') {
		return config;
	}

	try {
		const token = await getJWT();

		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
	} catch (error) {
		console.error('Error while fetching jwt', error);

		// Always return the config so subsequent calls can be made without the token
		return config;
	}

	return config;
}

export const onSuccess = (response: AxiosResponse) => response.data;
export const onError = (error: AxiosError) => {
	if (error.response) {
		// The request was made and the server responded with a status code
		// that falls out of the range of 2xx
		throw error.response.data;
	} else if (error.request) {
		// The request was made but no response was received
		// `error.request` is aninstance of XMLHttpRequest
		throw new Error('Server is not responding, please try again');
	}

	// Something happened in setting up the request that triggered an Error
	throw error.message;
};

export const headersNoCache = {
	'Cache-Control': 'no-cache',
};

export const headersDefault = {
	'x-csrf-token': CSRF,
};

export const axiosHeaders = axios.create({ headers: headersDefault });
export const axiosWithJWT = axios.create({ headers: headersDefault });

axiosWithJWT.interceptors.request.use(
	(config) => addToken(config),
	(error) => {
		return Promise.reject(error);
	},
);

const customAxios = (jwt?: boolean) => (jwt ? axiosWithJWT : axiosHeaders);

export function get({
	url,
	config,
	jwt,
}: {
	url: string;
	config?: AxiosRequestConfig;
	jwt?: boolean;
}) {
	return customAxios(jwt).get(url, config).then(onSuccess).catch(onError);
}

export function post({
	url,
	data,
	config,
	jwt,
}: {
	url: string;
	data?: unknown;
	config?: AxiosRequestConfig;
	jwt?: boolean;
}) {
	return customAxios(jwt).post(url, data, config).then(onSuccess).catch(onError);
}

export function put({
	url,
	data,
	config,
	jwt,
}: {
	url: string;
	data: unknown;
	config?: AxiosRequestConfig;
	jwt?: boolean;
}) {
	return customAxios(jwt).put(url, data, config).then(onSuccess).catch(onError);
}

export function patch({ url, data, jwt }: { url: string; data: unknown; jwt?: boolean }) {
	return customAxios(jwt).patch(url, data).then(onSuccess).catch(onError);
}

export function deletes({
	url,
	data,
	jwt,
}: {
	url: string;
	data?: AxiosRequestConfig;
	jwt?: boolean;
}) {
	return customAxios(jwt).delete(url, data).then(onSuccess).catch(onError);
}

export const defaultFormHeaders = {
	'Content-Type': 'multipart/form-data',
	'x-requested-with': 'XMLHttpRequest',
};
